import React, { useState, useCallback } from 'react';
import { IconButton, Link } from '@mui/material';
import { TopRatedBadge } from '../badges/top-rated-badge/top-rated-badge';
import { NewServiceBadge } from '../badges/new-service-badge/new-service-badge';
import { PromotedBadge } from '../badges/promoted-badge/promoted-badge';
import { CriminalCheckBadge } from '../badges/criminal-record-check-badge/criminal-record-check-badge';
import { cn } from '@src/utils/ui';
import PropTypes from 'prop-types';
import { ServicePropType } from './prop-types/service';
import { generateSlug } from '@src/utils/strings';
import toastr from 'toastr';
import { MemberBadge } from '../member-badge/member-badge';
import { GovIdVerifiedBadge } from '../gov-id-verified-badge/gov-id-verified-badge';
import { getOptimizedImageUrl, getItemSrcSet, handleImageError } from '@src/utils/getOptimizedImageUrl';
import { AttachMoney, Favorite, LocationOn, Star } from '@mui/icons-material';

function formatCount(count) {
    if (!count) {
        return 0;
    }

    if (count >= 1_000_000) return (count / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (count >= 1_000) return (count / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
    return count.toString();
}

const styles = {
    icon: {
        size: 16,
        weight: 'fill',
        color: '#5A5A5A'
    },
    subDetail: 'tw-flex tw-items-center tw-gap-[4px]'
};

const getItemUrl = (itemName, itemId) => {
    return '/items/' + generateSlug(itemName) + '/' + itemId;
};

export const ServiceCard = ({ service, onFavorite, onUnFavorite, userID = null, onFavorited = null }) => {
    const {
        id,
        name,
        price,
        priceLabel,
        location,
        imageUrl,
        categoryName,
        badges = {},
        verifications = {},
        ratings,
        ratingsCount
    } = service;

    const [isFavorited, setIsFavorited] = useState(service.isFavorited);
    const [wishlistId, setWishlistId] = useState(service.wishlistId);
    const { isCriminalRecordVerified, isGovIdVerified } = verifications ?? {};

    const formattedRatingsCount = formatCount(ratingsCount);
    const showBadges = Object.values({ ...badges, isCriminalRecordVerified }).some(Boolean);
    const formattedPrice = '$' + Number(price).toFixed(2);
    const url = getItemUrl(name, id);

    const handleSuccess = response => {
        console.log('handleSuccess favorite', { response });

        if (!response.success) {
            toastr.error('Please try again later.', 'Oops! Something went wrong.');
        } else {
            if (response.item?.id) {
                setWishlistId(response.item.wishlistId);
            }

            setIsFavorited(prev => !prev);

            if (onFavorited) {
                onFavorited();
            }
        }
    };

    const handleFavoriteClick = event => {
        event.preventDefault();
        event.stopPropagation();

        if (isFavorited) {
            onUnFavorite({ wishlistId }, handleSuccess);
        } else {
            onFavorite(
                {
                    ItemDetail: {
                        ID: id
                    }
                },
                handleSuccess
            );
        }
    };

    return (
        <Link
            href={url}
            underline='none'
            sx={{
                '&:hover': { textDecoration: 'none' },
                '&:hover *': { textDecoration: 'none' }
            }}
            className={cn(
                'service-card tw-relative tw-min-w-[240px] tw-max-w-[450px] tw-w-full',
                'tw-flex tw-flex-col tw-items-start tw-flex-1 tw-self-stretch',
                'tw-border tw-border-primary-500 tw-bg-white tw-rounded-none'
            )}
        >
            {/* Image Section */}
            <div className='sc-image_container tw-flex tw-h-[200px] tw-flex-col tw-justify-between tw-isolate tw-items-center tw-gap-[10px] tw-self-stretch tw-relative'>
                <img
                    // TODO: Remove this ref once search items are fetched client-side.
                    // It compensates for SSR hydration timing: the image 404s before
                    // React attaches the onError listener.
                    ref={(img) => {
                        if (img && img.complete && img.naturalWidth === 0 && !img.dataset.fellBack) {
                            handleImageError({ target: img }, imageUrl);
                        }
                    }}
                    src={getOptimizedImageUrl('item', id, 0, 'thumb') || imageUrl}
                    srcSet={getItemSrcSet(id, 0) || undefined}
                    sizes='(max-width: 768px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw'
                    className='sc-image_container tw-h-[200px] tw-absolute tw-w-full tw-top-0 tw-left-0 tw-object-cover tw-z-[-1]'
                    alt={categoryName}
                    loading='lazy'
                    decoding='async'
                    width={450}
                    height={200}
                    onError={(e) => handleImageError(e, imageUrl)}
                />
                {badges.isShowBCAAMember && <MemberBadge className='tw-absolute tw-top-[-1px] tw-left-0 tw-h-[30px]' />}
                <div className='tw-flex tw-px-[4px] tw-pt-[4px] tw-pb-0 tw-justify-between tw-items-center tw-self-stretch'>
                    <div className='sc-ratings tw-invisible tw-h-[28px] tw-w-[80px]'></div>

                    {userID && (
                        <IconButton
                            aria-label='Favorite service'
                            className='sc-favorite tw-flex tw-w-[32px] tw-h-[32px] tw-flex-shrink-0 tw-bg-white hover:tw-bg-white'
                            onClick={handleFavoriteClick}
                        >
                            <Favorite
                                size={16}
                                weight='fill'
                                className={cn(isFavorited ? 'tw-text-red-500' : 'tw-text-[#D9D9D9]')}
                            />
                        </IconButton>
                    )}
                </div>
                <div className='tw-flex tw-px-[4px] tw-pb-[4px] tw-pt-0 tw-justify-between tw-items-center tw-self-stretch'>
                    {isGovIdVerified && <GovIdVerifiedBadge className='tw-w-[24px] tw-aspect-square' />}
                </div>
            </div>

            {/* Details */}
            <div className='sc-details tw-flex tw-flex-col tw-items-start tw-self-stretch'>
                <div className='tw-w-full sc-main_details tw-flex tw-flex-col tw-font-bcaasans-regular'>
                    <div className='sc-name tw-flex tw-p-[16px] tw-items-center tw-self-stretch'>
                        <span className='tw-font-semibold tw-text-[14px] tw-leading-[133%] tw-text-primary-500 tw-self-stretch tw-whitespace-normal tw-break-words'>
                            {name}
                        </span>
                    </div>

                    <div
                        className={cn(
                            'sc-sub_details tw-flex tw-flex-col tw-gap-[8px] tw-text-[#808080] tw-text-[14px] tw-tracking-[0.17px]',
                            'tw-flex-1 tw-pl-[16px] tw-pr-[16px] tw-pb-[16px]'
                        )}
                    >
                        <div className={styles.subDetail}>
                            <AttachMoney {...styles.icon} />
                            <span className='tw-leading-[133%] tw-align-middle'>
                                <span className='tw-font-[700]'>{formattedPrice}</span>
                                <span> {priceLabel}</span>
                            </span>
                        </div>
                        <div className={cn([styles.subDetail, 'tw-whitespace-normal tw-break-words'])}>
                            <LocationOn {...styles.icon} />
                            <div className='tw-grow tw-w-[1px]'>{location}</div>
                        </div>
                        {ratingsCount >= 1 ? (
                            <div className={cn([styles.subDetail, 'tw-break-all'])}>
                                <Star size={16} weight='fill' className='tw-text-yellow-500' />
                                <span className='tw-font-[500] tw-tracking-[0.46px] tw-leading-[22px] tw-align-middle tw-text-[13px]'>
                                    {ratings} ({formattedRatingsCount})
                                </span>
                            </div>
                        ) : null}
                    </div>
                </div>

                {/* Conditional Badges */}
                {showBadges && (
                    <div className='badges tw-flex tw-p-[16px] tw-pt-0 tw-items-start tw-content-start tw-gap-[8px] tw-self-stretch tw-flex-wrap'>
                        {badges.isTopRated && <TopRatedBadge />}
                        {badges.isNewService && <NewServiceBadge />}
                        {badges.isPromoted && <PromotedBadge />}
                        {isCriminalRecordVerified && <CriminalCheckBadge />}
                    </div>
                )}
            </div>
        </Link>
    );
};

ServiceCard.propTypes = {
    service: ServicePropType.isRequired,
    onFavorited: PropTypes.func,
    onFavorite: PropTypes.func.isRequired,
    onUnFavorite: PropTypes.func.isRequired,
    userID: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null, undefined])])
};
